import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { prop } from 'ramda';
import { useAppSelector } from 'js/store/hooks';
import { useTranslations } from 'js/hooks/i18n';
import { Icon } from 'js/components/common/Icon';
import { I18n } from 'js/components/common/I18n';
import { useIsPageInPopupUrl } from 'js/components/system/Page/hooks';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import * as Types from './types';

import style from './Tooltip.scss';

export const Tooltip: React.FC<Types.ITooltipProps> = ({ content, iconType, className }) => {
    const translate = useTranslations();
    const isPageInPopupUrl = useIsPageInPopupUrl();
    const arrowRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const headerHeight = useAppSelector(prop('headerHeight'));
    const [isTooltipOpened, setIsTooltipOpened] = useState<boolean>(false);

    const toggleTooltip = useCallback((event: React.PointerEvent<HTMLElement>): void => {
        event.preventDefault();
        event.stopPropagation();

        setIsTooltipOpened(!isTooltipOpened);
    }, [setIsTooltipOpened, isTooltipOpened]);

    const handleBlur = useCallback((event: Event): void => {
        !tooltipRef.current?.contains(event.target as HTMLElement) && setIsTooltipOpened(false);
    }, [setIsTooltipOpened]);

    const onTooltipContentClick = (event: React.PointerEvent<HTMLElement>): void => {
        event.preventDefault();
        event.stopPropagation();
    };

    useEffect(() => {
        window.addEventListener('click', handleBlur);

        return () => {
            window.removeEventListener('click', handleBlur);
        };
    }, [handleBlur]);

    useLayoutEffect(() => {
        if (isTooltipOpened && tooltipRef.current && arrowRef.current) {
            let top;
            const { top: tooltipTop, right: tooltipRight } = tooltipRef.current.getBoundingClientRect();
            const { top: portletTop, right: portletRight } = tooltipRef.current
                .closest('.fn-portlet-wrapper')?.getBoundingClientRect() as DOMRect;

            if (isPageInPopupUrl()) {
                const popupTitle = tooltipRef.current?.closest('.popup-modal__inner-content')
                    ?.previousSibling as HTMLElement;
                const popupTitleHeight = popupTitle?.offsetHeight;

                top = popupTitleHeight > portletTop ? popupTitleHeight : portletTop;
            } else {
                top = headerHeight && headerHeight > portletTop ? headerHeight : portletTop;
            }

            tooltipRef.current.classList.toggle(style.tooltipBottom, tooltipTop < top);
            arrowRef.current.classList.toggle(style.arrowBottom, tooltipTop < top);

            if (tooltipRight > portletRight) {
                tooltipRef.current.style.left = `calc(-2rem - ${tooltipRight - portletRight}px)`;
            }
        }
    }, [headerHeight, isTooltipOpened, isPageInPopupUrl]);

    const tooltipContent = typeof content === 'string'
        ? <I18n tKey={content} defaultValue={content} />
        : content;

    return (
        <div className={style.container}>
            <Icon
                className={className}
                onClick={toggleTooltip}
                type={iconType || 'dynamicFormTooltip'}
                aria-label={translate('playtech.mobile.system.tooltip.aria-label')}
            />
            {isTooltipOpened && (
                <span onClick={onTooltipContentClick}>
                    <div ref={arrowRef} className={style.arrow} />
                    <AccessibilityElement className={style.popover} elementRef={tooltipRef}>
                        {tooltipContent}
                    </AccessibilityElement>
                </span>
            )}
        </div>
    );
};
